<template>
  <div>
    <div class="content">
      <h2 class="title">
        <awesomeIcon icon="seedling" /> Duurzaamheid
      </h2>
      <p>
        Op basis van de antwoorden die u tot hier heeft gegeven, selecteren wij de optimale
        beleggingsportefeuille voor u. Voordat we dat kunnen, willen wij ook weten of u
        duurzaamheidsvoorkeuren heeft.
        <br><br>
          Om dat goed te kunnen doen geven we u eerst informatie over de begrippen die gebruikt
          worden als het gaat om duurzaamheidsvoorkeuren. Het is van belang dat u deze informatie
          goed leest voordat u aangeeft of u duurzaamheidsvoorkeuren heeft.
      </p>
      <div class="content scroll-border">
        <h2>Informatie en begrippen duurzaamheidsvoorkeuren</h2>
        Hieronder volgt de uitleg bij de begrippen die horen bij duurzaamheidsvoorkeuren.
        <br /><br />
        <div class="content scroll-container">
          <h3>Duurzaamheidsvoorkeuren</h3>
          De duurzaamheidsvoorkeuren worden ingevuld aan de hand van uw voorkeuren voor drie type
          beleggingen:
          <ol>
            <li>
              <b>Ecologische duurzame belegging</b> - dit zijn beleggingen in bedrijven die helpen
              bij het tegengaan van klimaatverandering en voldoen aan door de wetgever
              vastgestelde strenge criteria;
            </li>
            <li>
              <b>Duurzame belegging</b> - dit zijn beleggingen in bedrijven die helpen bij het
              bereiken van een milieudoelstelling of een sociale doelstelling waarbij het bedrijf
              praktijken van goed ondernemingsbestuur (zoals integer gedrag, hoog niveau van
              transparantie en verantwoording) volgt;
            </li>
            <li>
              <b>Negatieve effecten op duurzaamheidsfactoren meegewogen belegging.</b>
            </li>
          </ol>
          Het onderscheid tussen die drie beleggingen, leggen we hieronder verder aan u uit.
          Het onderscheid geldt overigens zowel voor directe beleggingen in bedrijven als
          indirecte beleggingen (via beleggingsfondsen).
          <ol>
            <li><b>Ecologisch duurzame belegging</b></li>
            Dit zijn beleggingen in bedrijven die helpen bij het tegengaan van klimaatverandering
            of het beperken van de gevolgen ervan.<br />
            Of een bedrijf ecologisch duurzame activiteiten heeft, wordt bepaald door de
            EU-Taxonomiewetgeving. Dit is een Europees regelgevend classificatiesysteem dat
            vaststelt of een belegging wel of niet bijdraagt aan een beter milieu. De EU-Taxonomie
            heeft verschillende strenge criteria vastgesteld waar een onderdeel of productielijn
            van een bedrijf aan moet voldoen om als ‘ecologisch duurzaam’ te mogen worden
            aangemerkt. Op het moment dat een bedrijf aan de eisen van de EU-Taxonomie kan voldoen
            mag een belegging in dat bedrijf meetellen als een ecologische duurzame belegging.
            <br /><br />
            <i>Een voorbeeld:</i><br />
            Een windmolenpark bouwen in een beschermd natuurgebied draagt bij aan het oplossen van
            het klimaatprobleem. Maar het veroorzaakt ook schade aan de natuur en zal dus niet
            aan alle eisen van de EU Taxonomie voldoen. Dit windmolenpark zal om die reden niet
            classificeren als ‘ecologisch duurzaam’.
            <br /><br />
            <i>Een ander voorbeeld:</i><br />
            Een kledingmerk, bijvoorbeeld Adidas, maakt in één van haar productielijnen
            uitsluitend gebruik van gerecycled plastic. We nemen aan dat deze productielijn
            (economische activiteit) volledig voldoet aan alle strenge Taxonomievereisten. Naast
            deze productielijn kent Adidas nog andere bedrijfsonderdelen die niet aan de
            Taxonomiecriteria voldoen. Een belegging in een aandeel Adidas zal daardoor
            gedeeltelijk worden beschouwd als ‘ecologisch duurzaam’.
            <br /><br />
            <li><b>Duurzame belegging</b></li>
            Het begrip duurzame belegging wordt beschreven in de SFDR-regelgeving
            (Sustainable Finance Disclosure Regulation). Dit is Europese regelgeving die
            voorschrijft hoe financiële instellingen beleggers moeten informeren over
            duurzaamheid. Een belegging kan slechts duurzaam worden genoemd, als het een concrete
            doelstelling nastreeft op het gebied van milieu (E) of maatschappij (S) en ook wordt
            voldaan aan goed ondernemingsbestuur ook wel good governance (G). Ook hier geldt dat
            met het bijdragen aan bijvoorbeeld een milieudoelstelling geen schade mag worden
            aangericht aan andere E en S doelstellingen.
            <br /><br />
            <i>Een voorbeeld:</i><br />
            Als het windmolenpark wordt gebouwd door arbeiders die onder slechte
            arbeidsomstandigheden moeten werken en leven dan zal de belegging niet als duurzaam
            kunnen meetellen.
            <br /><br />
            <li><b>Negatieve effecten op duurzaamheidsfactoren meegewogen belegging</b></li>
            Dit zijn beleggingen waarbij rekening wordt gehouden met de belangrijkste nadelige
            effecten op duurzaamheidsfactoren. Duurzaamheidsfactoren hebben betrekking op een
            breed scala aan ecologische-, sociale- en werkgelegenheidszaken. Denk bijvoorbeeld aan
            de eerbiediging van mensenrechten of het behoud van de biodiversiteit. Deze
            belangrijkste nadelige effecten worden ook wel de Principal Adverse Impacts
            (of afgekort: PAIs) genoemd. Elk bedrijf heeft een impact op het milieu (E) en de
            maatschappij (S). Deze impact kan positief of negatief zijn.
            <br /><br />
            <i>Een voorbeeld:</i><br />
            Een olieconcern dat opnieuw investeert in fossiele brandstof. Het nadelige gevolg of
            ook wel negatieve effect van een belegging in dit bedrijf is een verhoogde
            CO2-uitstoot. Zou het olieconcern investeren in een windmolenpark in plaats van
            fossiele brandstof dan zou de negatieve impact van de belegging op het milieu lager
            uitpakken.<br /><br />
            Het meewegen van dergelijke negatieve effecten op duurzaamheidsfactoren gebeurt veelal
            kwalitatief, bijvoorbeeld door uitsluiting of door engagement.
            <br /><br />
            Uitsluiting betekent dat (groepen) bedrijven worden uitgesloten van belegging op basis
            van duurzaamheidskarakteristieken, bijvoorbeeld beleggingen in tabak, wapens of bont.
            <br /><br />
            Engagement wordt gezien als een langdurige actieve dialoog tussen beleggers en het
            bedrijf waarin wordt belegd. Deze actieve dialoog biedt beleggers de mogelijkheid om
            de mogelijke duurzaamheidsrisico’s en -kansen te bespreken met deze bedrijven. Dit kan
            plaatsvinden in samenwerkingsverband, om zo meer impact te maken. Voorbeelden hiervan
            zijn: Eumedion en Climate Action 100+.
            <br /><br />
            Het is ook mogelijk om de PAI op een kwantitatieve (cijfermatige) manier mee te nemen.
            In dat geval wordt gebruik gemaakt van zogenaamde PAI-indicatoren. Een PAI-indicator
            berekent de impact van een belegging op de milieu-, sociale en governance
            (ESG)-factoren. Denk aan CO2-uitstoot, blootstelling aan fossiele brandstoffen, water
            en afval (E), zorgvuldigheid op gebied van mensenrechten of genderdiversiteit (S),
            blootstelling aan corruptie, omkoping of andere schandalen (G).
          </ol>
          <h3>ESG</h3>
          Vaak als wordt gesproken over duurzaamheid, dan wordt de afkorting ESG gebruikt. Dit
          staat voor Environmental, Social en Governance.
          <ul>
            <li>Environmental (<b>E</b>) staat voor milieu.</li>
            Denk hierbij bijvoorbeeld aan klimaatverandering, CO2-uitstoot, afvalverwerking en
            watergebruik.
            <li> Social (<b>S</b>) staat voor maatschappij.</li>
            Denk hierbij bijvoorbeeld aan werkomstandigheden, mensenrechten en toegang tot zorg.
            <li> Governance (<b>G</b>) staat voor ondernemingsbestuur.</li>
            Dit gaat over zaken als diversiteit in het bestuur, beloningsbeleid, naleving wet-
            en regelgeving en aandeelhoudersbescherming.
          </ul>
        </div>
        <customInput
          style="border: 1px solid grey; margin-top: 20px; margin-bottom: 20px;"
          v-model="modelValue.acceptSustainabilityText"
          type="yesno2"
          :label="'Ik heb bovenstaande informatie gelezen'"
          boxtype="checkbox"
        />
      </div>
      <template v-if="brandName == 'Doelbeleggen'">
        <p class="subheader">Heeft u duurzaamheidsvoorkeuren?</p>
        <customInput
          v-for="(option, key) in sustainabilityPreferences"
          v-model="modelValue.sustainabilityNew"
          :key="key + 'sustainability'"
          type="radio2"
          name="radiogroup1"
          :label="option.label"
          :option="option.value"
        />

        <template v-if="modelValue.sustainabilityNew === 'Ja, specifiek'">
          <p class="subheader">
            Heeft u voorkeuren voor (1) ecologisch- en/of (2) duurzame beleggingen?
          </p>
          <customInput
            v-for="(option, key) in sustainabilityEcologicPreferences"
            v-model="modelValue.sustainabilityEcologicPreference"
            :key="key + 'preference'"
            type="radio2"
            name="radiogroup2"
            :label="option.label"
            :option="option.value"
          />
        </template>

        <template
          v-if="
            modelValue.sustainabilityNew === 'Ja, specifiek'
            && modelValue.sustainabilityEcologicPreference === 'Nee'
          "
        >
          <p class="subheader">
            Wilt u dat wij rekening houden met (3) negatieve effecten op duurzaamheidsfactoren?
          </p>
          <customInput
            v-for="(option, key) in sustainabilityEffectPreferences"
            v-model="modelValue.sustainabilityEffect"
            :key="key + 'effect'"
            type="radio2"
            name="radiogroup3"
            :label="option.label"
            :option="option.value"
          />
        </template>

        <template
          v-if="
            modelValue.sustainabilityNew === 'Ja, specifiek'
            && modelValue.sustainabilityEcologicPreference === 'Nee'
            && modelValue.sustainabilityEffect === 'Ja, PAI'
          "
        >
          <p class="subheader">Op welk gebied wilt u PAI toepassen?</p>
          <p class="msg">Let op, meerdere antwoorden zijn mogelijk</p>
          <div class="flex-container flex-grow">
            <ul style="list-style-type: none; margin:0; padding: 0;">
              <li>
                <customInput
                  v-model="modelValue.sustainabilityPaiEnvironment"
                  type="yesno2"
                  label="Op het gebied van milieu (E)"
                  boxtype="checkbox"
                />
              </li>
              <li>
                <customInput
                  v-model="modelValue.sustainabilityPaiSocial"
                  type="yesno2"
                  label="Op het gebied van maatschappij (S)"
                  boxtype="checkbox"
                />
              </li>
              <li>
                <customInput
                  v-model="modelValue.sustainabilityPaiManagement"
                  type="yesno2"
                  label="Op het gebied van ondernemingsbestuur (G)"
                  boxtype="checkbox"
                />
              </li>
            </ul>
          </div>
          <p class="subheader">
            Wilt u dit kwalitatief of kwantitatief laten meewegen?
          </p>
          <customInput
            v-for="(option, key) in sustainabilityPaiConsiderations"
            v-model="modelValue.sustainabilityPaiConsider"
            :key="key + 'paiConsider'"
            type="radio2"
            name="radiogroup4"
            :label="option.label"
            :option="option.value"
          />
        </template>
        <div
          v-if="$parent.sustainabilityPopup === 'A'"
          class="content popup-text-yellow"
        >
          U geeft aan dat u geen duurzaamheidsvoorkeuren heeft. We richten uw
          portefeuille in op basis van uw risicoprofiel. Wij bieden een portefeuille die
          ecologische en/of sociale kenmerken promoot. De kenmerken die in de portefeuille
          van ons worden gepromoot zijn een gezondere leefomgeving. Wij sluiten overtreders
          van de UN Global Compact Principes uit en beleggen niet in de productie van tabak.
        </div>
        <div
          v-else-if="$parent.sustainabilityPopup === 'B'"
          class="content popup-text-yellow"
        >
          U geeft aan duurzaamheidsvoorkeuren te hebben maar die heeft u niet gespecificeerd. Wij
          bieden een portefeuille die ecologische en/of sociale kenmerken promoot. De kenmerken die
          in de portefeuille van ons worden gepromoot zijn een gezondere leefomgeving. Wij sluiten
          overtreders van de UN Global Compact Principes uit en beleggen niet in de productie van
          tabak.
        </div>
        <div
          v-else-if="$parent.sustainabilityPopup === 'C'"
          class="content popup-text-yellow"
        >
          Bij de beantwoording van de vragen heeft u specifieke duurzaamheidsvoorkeuren
          aangegeven. Wij bieden een portefeuille die ecologische en/of sociale kenmerken promoot.
          De kenmerken die in de portefeuille van ons worden gepromoot zijn een gezondere
          leefomgeving. Wij sluiten overtreders van de UN Global Compact Principes uit en beleggen
          niet in de productie van tabak. Hiermee wordt niet of niet volledig aan uw
          duurzaamheidsvoorkeuren voldaan.
          <template v-if="!$parent.redo">
            Als u toch graag wenst te beleggen bij ons kunt u uw
            duurzaamheidsvoorkeuren aanpassen en/of uw aanvraag alsnog aan ons versturen. Wij zullen
            voor u dan een beleggingsplan opstellen dat aansluit op uw risicoprofiel en de
            duurzaamheidskenmerken zoals hiervoor genoemd maar niet op uw duurzaamheidsvoorkeuren.
            Als u het beleggingsplan aanvaardt, stemt u hiermee in. U kunt er uiteraard ook nog voor
            kiezen om niet akkoord te gaan met het beleggingsplan.
          </template>
          <template v-else>
            Als u toch graag door wilt met beleggen bij ons kunt u uw duurzaamheidsvoorkeuren
            aanpassen en/of uw wijzigingen aan ons versturen. Wij zullen voor u dan een nieuw
            beleggingsplan opstellen dat aansluit op uw risicoprofiel en de duurzaamheidskenmerken
            zoals hiervoor genoemd maar niet op uw duurzaamheidsvoorkeuren. Met het insturen van uw
            wijziging, stemt u hiermee in. Wilt u dit niet, verstuur dan uw wijziging niet en neem
            contact met ons op.
          </template>
        </div>
      </template>
      <template v-else>
        <p class="subheader"> Heeft u duurzaamheidsvoorkeuren met het oog op duurzaam beleggen?</p>
        <customInput
          v-model="modelValue.sustainability"
          type="radio2"
          name="radiogroup1"
          label="Ik wil uitsluitend duurzaam beleggen en heb dan ook duurzaamheidsvoorkeuren."
          option="A"
        />
        <customInput
          v-model="modelValue.sustainability"
          type="radio2"
          name="radiogroup1"
          label="Ik heb geen interesse in duurzaam beleggen en heb geen duurzaamheidsvoorkeuren."
          option="B"
        />
        <customInput
          v-model="modelValue.sustainability"
          type="radio2"
          name="radiogroup1"
          :label="`Ik vind duurzaam beleggen niet onbelangrijk, maar wil graag bij ${
            brandName
          } beleggen.`"
          option="C"
        />
        <div
          v-if="modelValue.sustainability === 'A'"
          class="content popup-text-yellow"
        >
          <p>
            U wilt duurzaam beleggen en heeft duurzaamheidsvoorkeuren die tot uitdrukking moeten
            komen in de wijze waarop wij voor u gaan beleggen. Helaas kunnen wij u niet voorzien
            van een beleggingsportefeuille die aan uw duurzaamheidsvoorkeuren tegemoetkomt. Graag
            lichten wij dit toe.
            <br /><br />
            Wij hanteren een algemeen beleid ten aanzien van de selectie van beleggingen
            waarin cliënten kunnen investeren. We werken op dit moment met een aantal verschillende
            beleggingsportefeuilles. Deze worden samengesteld uit trackers (ETF’s) die we selecteren
            op basis van verschillende criteria zoals bijvoorbeeld het type onderliggende waarde,
            verhandelbaarheid en kosten. Duurzaamheidsfactoren of ESG-criteria zijn daarbij nog niet
            doorslaggevend.
            <br /><br />
            <template v-if="!$parent.redo">
              Als u desondanks toch graag wenst te beleggen bij ons, dan kunt u door naar het
              overzicht en uw aanvraag alsnog aan ons versturen. Wij zullen voor u dan een
              beleggingsplan opstellen dat aansluit op uw risicoprofiel maar niet op uw
              duurzaamheidsvoorkeuren. Als u dit beleggingsplan later in het traject aanvaardt,
              stemt u hiermee in. U kunt er uiteraard ook nog voor kiezen om niet akkoord te gaan
              met het beleggingsplan.
            </template>
            <template v-else>
              Als u desondanks toch graag wenst te beleggen bij ons, dan kunt u door naar het
              overzicht en uw wijziging alsnog aan ons versturen. Wij zullen voor u dan een
              beleggingsplan opstellen dat aansluit op uw risicoprofiel maar niet op uw
              duurzaamheidsvoorkeuren. Met het insturen van uw wijziging, stemt u hiermee in.
              <br /><br />
              Wilt u dit niet, verstuur dan uw wijziging niet en neem contact met ons op.
            </template>
          </p>
        </div>
        <div
          v-else-if="modelValue.sustainability === 'B' || modelValue.sustainability === 'C'"
          class="content popup-text"
        >
          <p>
            {{modelValue.sustainability === 'B'
              ? 'U heeft aangegeven dat u geen duurzaamheidsvoorkeuren heeft.'
              : `U heeft aangegeven duurzaam beleggen niet onbelangrijk te vinden, maar financieel
                  rendement en de dienstverlening van ons belangrijker. Om die reden heeft u
                  geen duurzaamheidsvoorkeuren.`
            }}
            Wij zullen daarom een beleggingsplan opstellen dat geen rekening houdt met
            duurzaamheidsvoorkeuren.
          </p>
        </div>

        <modalWindow v-if="infoModal" @close="infoModal = false" width="600">
          <div class="titlebar">Duurzaam beleggen</div>
          <div class="inset">
            <h3 class="green-color">Duurzaam {{$parent.intakeAdmin.beleggen.brand}}</h3>
            Nog dit jaar(2022) zetten wij een belangrijke stap en verandert het beleggingsbeleid.
            Daarmee willen wij bijdragen aan een gezondere samenleving, een veiligere leefomgeving
            en een toekomstbestendige maatschappij. Dat doen we omdat we dat zelf belangrijk vinden
            en omdat onze klanten erom vragen.<br />
            <h3 class="green-color">Nieuw beleggingsbeleid</h3>
            Ons nieuwe beleggingsbeleid noemt men ook wel ’lichtgroen’. Dat houdt in dat we bij de
            keuze van de beleggingen aanvullende selectiecriteria toepassen op het gebied van
            Environment, Social en Governance (ESG). Of anders gezegd het milieu, goede sociale
            omstandigheden en behoorlijk bestuur. Zo zullen wij fondsen selecteren die voldoen aan
            ’good governance’ en niet beleggen in tabak. Daarbij worden fondsen geselecteerd die
            ondernemingen uitsluiten die de 10 ESG principes van het UN Global Compact overtreden.
            Alle fondsen uit uw portefeuille worden straks beoordeeld op bovenstaande criteria.
            Wij gaan hierbij af op de toetsing van MCSI, een daarvoor deskundig instituut voor
            beleggingsonderzoek. Daarnaast blijven we natuurlijk net als voorheen verboden
            beleggingen uitsluiten.<br /><br />
          </div>
        </modalWindow>
      </template>
    </div>
  </div>
</template>
<script>
export default {
  name: 'IntakeSustainabilityPage',
  emits: ['update:model-value'],
  props: {
    modelValue: Object,
  },
  data() {
    return {
      sustainabilityPreferences: [
        { label: 'Nee', value: 'Nee' },
        { label: 'Ja, maar ik heb geen specifieke voorkeuren', value: 'Ja, niet specifiek' },
        { label: 'Ja, ik heb specifieke voorkeuren', value: 'Ja, specifiek' },
      ],
      sustainabilityEcologicPreferences: [
        { label: 'Nee', value: 'Nee' },
        {
          label: 'Ja, ik heb een voorkeur voor (1) ecologisch duurzame beleggingen',
          value: 'Ja, ecologisch duurzame beleggingen',
        },
        {
          label: 'Ja, ik heb een voorkeur voor (2) duurzame beleggingen',
          value: 'Ja, duurzame beleggingen',
        },
        { label: 'Ja, ik heb een voorkeur voor beide', value: 'Ja, allebei' },
      ],
      sustainabilityEffectPreferences: [
        { label: 'Nee', value: 'Nee' },
        {
          label: 'Ja, maar er hoeft geen PAI indicator/methode gebruikt te worden',
          value: 'Ja, geen PAI',
        },
        { label: 'Ja, en graag volgens de PAI indicator/methode', value: 'Ja, PAI' },
      ],
      sustainabilityPaiConsiderations: [
        { label: 'Geen van beide', value: 'Geen' },
        { label: 'Kwalitatief', value: 'Kwalitatief' },
        { label: 'Kwantitatief', value: 'Kwantitatief' },
        { label: 'Kwalitatief en kwantitatief', value: 'Allebei' },
      ],
      infoModal: false,
    };
  },
  computed: {
    brandName() {
      if (this.$parent.intakeAdmin.beleggen.brand === 'VMVB') {
        return 'Velthuyse • Mulder';
      }
      if (this.$parent.intakeAdmin.beleggen.brand === 'BVS') {
        return 'Beer & Van Stapele';
      }
      if (this.$parent.intakeAdmin.beleggen.brand === 'OXBY') {
        return 'OXBY';
      }
      return 'Doelbeleggen';
    },
  },
};
</script>
<style lang="scss" scoped>
.popup-text {
  background: $color-grey;
  margin-top: 20px;
  padding: 20px;
  margin-bottom: 20px;
}
.popup-text-yellow {
  background: #fff3cd;
  margin-top: 20px;
  padding: 20px;
  margin-bottom: 20px;
}
.scroll-border {
  background: $color-grey;
  margin-bottom: 20px;
  margin-top: 20px;
  padding: 20px 20px 0 20px;
  border-radius: 5px;
  border: 2px solid #ccc;
}
.scroll-container {
  padding: 20px;
  background: $color-grey;
  width: 100%;
  max-height: 300px;
  overflow-y: scroll;
}
.green-color{
  color: $color-highlight;
}
</style>
